import { useCallback, useState } from 'react';

import { ApiFailure, request } from '../../shared/api';
import { EmptyState, ListSkeleton, renderFailure } from '../../shared/states';
import { useLoad } from '../../shared/use-load';
import { usePage } from '../../shared/use-page';
import { shareLink } from '../../telegram/webapp';

/**
 * Реферальный экран (T17.5, `05_API.md` §11.2).
 *
 * ## Ссылка приходит готовой
 *
 * Сервер отдаёт адрес целиком, а не код с указанием, что из него
 * строить. Приложений уже два, и каждое собирало бы адрес само;
 * разойтись им достаточно в одном знаке, чтобы половина приглашений
 * перестала засчитываться — причём молча, без всякой ошибки.
 *
 * ## Два баланса показываются рядом
 *
 * «Доступно» и «ждёт» — разные деньги, и человек, увидевший только
 * первое, решит, что вознаграждение не начислили. Срок защиты объясняет
 * разницу: до его истечения платёж могут вернуть, и выплатить процент с
 * возвращённых денег значило бы заплатить дважды.
 *
 * ## Отменённое остаётся в истории
 *
 * Пропавшая строка выглядит как потерянные деньги, и человек идёт в
 * поддержку выяснять, куда делось начисление, которое он вчера видел.
 */

interface Payout {
  readonly amount_minor: string;
  readonly status: string;
}

interface Summary {
  readonly link: string;
  readonly code: string;
  readonly invited: number;
  readonly paying: number;
  readonly available_minor: string;
  readonly pending_minor: string;
  readonly earned_minor: string;
  readonly currency: string;
}

interface Reward {
  readonly id: string;
  readonly amount_minor: string;
  readonly rate: string;
  readonly status: 'pending' | 'available' | 'cancelled' | 'reversed';
  readonly available_at: string;
  readonly created_at: string;
}

/** Сколько начислений за раз. */
const PAGE = 20;

/** Состояния начисления словами. Коды те же, что в схеме. */
const STATES: Record<Reward['status'], string> = {
  pending: 'ждёт',
  available: 'доступно',
  cancelled: 'отменено',
  reversed: 'возвращено',
};

function money(minor: string, currency: string): string {
  const major = Number(minor) / 100;
  const suffix = currency === 'RUB' ? ' ₽' : ` ${currency}`;

  return `${major.toLocaleString('ru-RU', { minimumFractionDigits: 2 })}${suffix}`;
}

function day(at: string): string {
  return new Date(at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

/** «10 %» из «0.100»: доля хранится числом, а читается процентом. */
function percent(rate: string): string {
  return `${String(Math.round(Number(rate) * 100))} %`;
}

export function ReferralScreen({ onBack }: { onBack: () => void }): React.JSX.Element {
  const [state, retry] = useLoad<Summary>(useCallback(() => request<Summary>('/v1/referral'), []));
  const history = usePage<Reward>('/v1/referral/rewards', PAGE);
  const [copied, setCopied] = useState(false);
  // Что случилось с последней заявкой: пусто — заявок не подавали
  const [payout, setPayout] = useState<{ done: boolean; text: string } | null>(null);
  const [sending, setSending] = useState(false);
  // Куда переводить. Номер карты не спрашиваем: перевод идёт по СБП, а
  // платёжные данные хранить ради вознаграждения несоразмерно
  const [phone, setPhone] = useState('');
  const [bank, setBank] = useState('');

  const withdraw = useCallback(() => {
    setSending(true);

    request<Payout>('/v1/referral/withdraw', {
      method: 'POST',
      body: { phone, bank },
    })
      .then((created) => {
        setPayout({
          done: true,
          text: `Заявка на ${money(created.amount_minor, 'RUB')} принята. Рассмотрим и сообщим в этом же чате.`,
        });
        // Доступное ушло в заморозку: показать прежнее число значило бы
        // предложить подать вторую заявку на те же деньги
        retry();
      })
      .catch((failure: unknown) => {
        setPayout({
          done: false,
          text:
            failure instanceof ApiFailure
              ? failure.message
              : 'Сервис не ответил. Это бывает; попробуйте ещё раз.',
        });
      })
      .finally(() => {
        setSending(false);
      });
  }, [bank, phone, retry]);

  const copy = useCallback((link: string) => {
    // Буфер обмена есть не везде и не всегда разрешён: отказ здесь не
    // повод показывать ошибку — ссылка видна на экране, её можно
    // выделить руками
    void navigator.clipboard?.writeText(link).then(
      () => {
        setCopied(true);
      },
      () => {
        setCopied(false);
      },
    );
  }, []);

  if (state.kind === 'loading') return <ListSkeleton rows={3} />;

  if (state.kind === 'failed') {
    return <>{renderFailure(state.error, { onRetry: retry, onUpgrade: retry })}</>;
  }

  const one = state.value;

  return (
    <>
      <button type="button" className="back" onClick={onBack}>
        ← Назад
      </button>

      <div className="card offer">
        <div className="offer__head">
          <span className="offer__name">Доступно</span>
          <span className="offer__price">{money(one.available_minor, one.currency)}</span>
        </div>

        {/*
          «Ждёт» показывается всегда, даже нулём: увидев только
          доступное, человек решит, что вознаграждение не начислили
        */}
        <p className="offer__note">
          Ждёт созревания {money(one.pending_minor, one.currency)} · заработано за всё время{' '}
          {money(one.earned_minor, one.currency)}
        </p>

        {payout === null ? null : (
          <p className={payout.done ? 'subscribe__hint' : 'subscribe__warning'}>{payout.text}</p>
        )}

        {/*
          Кнопка показывается всегда, а не только при достаточном
          остатке: спрятанная кнопка не объясняет, почему её нет, и
          человек ищет вывод в поддержке. Отключённая — объясняет сама,
          и подпись называет порог
        */}
        {/*
          Реквизиты спрашиваются на месте, а не в настройках: вывод
          случается редко, и поле, заполненное полгода назад, к моменту
          выплаты успевает устареть
        */}
        <div className="field">
          <label className="field__title" htmlFor="payout-phone">
            Телефон для перевода по СБП
          </label>
          <input
            id="payout-phone"
            className="input"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+7 900 000-00-00"
            value={phone}
            onChange={(event) => {
              setPhone(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__title" htmlFor="payout-bank">
            Банк получателя
          </label>
          <input
            id="payout-bank"
            className="input"
            type="text"
            placeholder="Т-Банк, Сбербанк, Альфа-Банк…"
            value={bank}
            onChange={(event) => {
              setBank(event.target.value);
            }}
          />
        </div>

        <button
          type="button"
          className="button button--accent"
          disabled={
            sending ||
            Number(one.available_minor) <= 0 ||
            phone.replace(/\D/gu, '').length < 10 ||
            bank.trim().length < 2
          }
          onClick={withdraw}
        >
          {sending ? 'Отправляю…' : 'Вывести'}
        </button>

        <p className="offer__note">
          Заявку рассматривает человек — ответ придёт в этот чат. Пока заявка на рассмотрении,
          деньги заморожены; при отказе они вернутся в доступные. Номер карты мы не спрашиваем:
          перевод идёт по СБП на указанный телефон.
        </p>
      </div>

      <div className="card subscribe">
        <p className="subscribe__title">Приглашайте друзей</p>
        <p className="subscribe__hint">
          Приглашено {one.invited}, из них платят {one.paying}. Вознаграждение приходит с каждой
          покупки подписки и пополнения кошелька; оно становится доступным через две недели — до
          этого платёж ещё могут вернуть.
        </p>

        <div className="field">
          <label className="field__title" htmlFor="referral-link">
            Ваша ссылка
          </label>
          <input id="referral-link" className="input" type="text" value={one.link} readOnly />
        </div>

        <div className="subscribe__actions">
          <button
            type="button"
            className="button button--accent"
            onClick={() => {
              shareLink(
                one.link,
                'Xoreva находит выгодные объявления об автомобилях раньше других. Заходи по ссылке',
              );
            }}
          >
            Поделиться
          </button>
          <button
            type="button"
            className="button"
            onClick={() => {
              copy(one.link);
            }}
          >
            {copied ? 'Скопировано' : 'Скопировать'}
          </button>
        </div>
      </div>

      <h2 className="screen__subtitle">История начислений</h2>

      {history.state === 'loading' ? <ListSkeleton rows={3} /> : null}

      {history.error !== null && history.items.length === 0
        ? renderFailure(history.error, { onRetry: history.reload, onUpgrade: history.reload })
        : null}

      {history.state !== 'loading' && history.error === null && history.items.length === 0 ? (
        <EmptyState
          title="Начислений пока нет"
          hint="Они появятся, когда приглашённый купит подписку или пополнит кошелёк."
        />
      ) : null}

      {history.items.map((reward) => (
        <div className="card menu menu--idle" key={reward.id}>
          <span className="menu__title">
            {money(reward.amount_minor, one.currency)} · {percent(reward.rate)}
          </span>
          <span className="menu__note">
            {STATES[reward.status]} ·{' '}
            {reward.status === 'pending'
              ? `будет доступно ${day(reward.available_at)}`
              : day(reward.created_at)}
          </span>
        </div>
      ))}

      {history.hasMore ? (
        <button
          type="button"
          className="button"
          disabled={history.state === 'more'}
          onClick={history.more}
        >
          {history.state === 'more' ? 'Загружаю…' : 'Показать ещё'}
        </button>
      ) : null}
    </>
  );
}
